import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, ChevronDown, CheckCircle, Zap, Wrench, Shield, Clock, ArrowRight } from 'lucide-react';
import DOMPurify from 'dompurify';
import { Helmet } from 'react-helmet-async';
import { PHONES, CONTACT, BLOG_POSTS } from '../content';
import { DISTRICT_PAGES } from '../data/districts';
import { getServiceBySlug, SERVICE_PAGES } from '../data/services-seo';
import { SEOHead, BreadcrumbSchema } from '../components/SEOHead';
import Breadcrumb from '../components/Breadcrumb';
import FinalCTA from '../components/FinalCTA';
import LiveBadge from '../components/LiveBadge';

const ICON_MAP = { Zap, Wrench, Shield, Clock, ArrowRight, CheckCircle };

const WA_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M5.077 18.828l.532-1.869A8.85 8.85 0 0 1 3.2 12C3.2 7.134 7.134 3.2 12 3.2c4.866 0 8.8 3.934 8.8 8.8 0 4.866-3.934 8.8-8.8 8.8a8.845 8.845 0 0 1-4.893-1.471l-2.03.499zM12 1C6.477 1 2 5.477 2 11c0 1.821.439 3.538 1.215 5.057L2 22l6.071-1.59A10.976 10.976 0 0 0 12 21c5.523 0 10-4.477 10-10S17.523 1 12 1z"/>
  </svg>
);

export default function ServiceDetailPage() {
  const location = useLocation();
  const slug = location.pathname.replace('/', '');
  const service = getServiceBySlug(slug);

  if (!service) return null;

  const Icon = ICON_MAP[service.icon] || Zap;
  const relatedBlogs = BLOG_POSTS.filter(p => service.relatedBlogs.includes(p.slug));
  const relatedDistricts = DISTRICT_PAGES.filter(d => service.relatedDistricts.includes(d.slug));
  const relatedServices = SERVICE_PAGES.filter(s => service.relatedServices.includes(s.slug));

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": service.faqs.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.h1,
    "description": service.metaDesc,
    "url": `https://724lastik.com.tr/${service.slug}`,
    "provider": {
      "@type": "LocalBusiness",
      "name": "7/24 Lastik Yol Yardim",
      "telephone": "+905349687611",
      "url": "https://724lastik.com.tr",
    },
    "areaServed": { "@type": "State", "name": "Kocaeli" },
  };

  return (
    <main data-testid={`service-page-${service.slug}`} style={{ background: 'var(--bg)' }}>
      <SEOHead
        path={`/${service.slug}`}
        title={service.title}
        description={service.metaDesc}
      />
      <BreadcrumbSchema items={[
        { name: 'Anasayfa', url: '/' },
        { name: 'Hizmetlerimiz', url: '/hizmetlerimiz' },
        { name: service.h1 },
      ]} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>

      {/* HERO */}
      <section style={{ background: 'var(--bg)', paddingTop: 86, borderBottom: '1px solid var(--border)' }}>
        <div className="wrap" style={{ padding: '3.5rem 1.5rem 3rem' }}>
          <Breadcrumb items={[
            { label: 'Anasayfa', to: '/' },
            { label: 'Hizmetlerimiz', to: '/hizmetlerimiz' },
            { label: service.h1 },
          ]} />

          <LiveBadge text="7/24 Aktif Hizmet" />

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: .5 }}
            style={{
              fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800,
              fontSize: 'clamp(2rem, 5.5vw, 3.5rem)', color: 'var(--text)',
              lineHeight: 1.05, letterSpacing: '-.04em', margin: '1rem 0 .5rem',
            }}
          >
            {service.h1}
          </motion.h1>
          <p style={{
            fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800,
            fontSize: 'clamp(1.2rem, 3.5vw, 2rem)', color: 'var(--red)',
            lineHeight: 1.1, letterSpacing: '-.03em', marginBottom: '1rem',
          }}>
            {service.h1Accent}
          </p>
          <p style={{ fontSize: 'clamp(.875rem, 1.5vw, 1rem)', color: 'var(--text2)', lineHeight: 1.7, maxWidth: 600 }}>
            {service.intro}
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.625rem', marginTop: '1.75rem' }}>
            <a href={PHONES.main_tel} data-testid="service-call-cta" className="btn btn-primary num ring" style={{ gap: '.5rem' }}>
              <Phone size={15} /> {PHONES.main}
            </a>
            <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer"
              data-gtm-action="whatsapp_click" data-testid="service-whatsapp-cta"
              className="btn btn-green" style={{ gap: '.4rem', fontSize: '.875rem' }}>
              {WA_ICON} WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="section" style={{ background: 'var(--bg)' }}>
        <div className="wrap">
          <div
            className="blog-content"
            style={{ maxWidth: 800, color: 'var(--text2)', lineHeight: 1.85, fontSize: '.95rem' }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(service.content) }}
          />
        </div>
      </section>

      {/* FAQ */}
      <section className="section" style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)' }}>
        <div className="wrap">
          <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
            <h2 className="h2">
              Sık Sorulan <span style={{ color: 'var(--blue)' }}>Sorular</span>
            </h2>
          </div>
          <div style={{ maxWidth: 740, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '.625rem' }}>
            {service.faqs.map((faq, i) => (
              <FAQItem key={faq.q} faq={faq} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* RELATED SERVICES */}
      {relatedServices.length > 0 && (
        <section className="section" style={{ background: 'var(--bg)', borderTop: '1px solid var(--border)' }}>
          <div className="wrap">
            <h2 className="h2" style={{ marginBottom: '1.5rem' }}>
              Diğer <span style={{ color: 'var(--green)' }}>Hizmetlerimiz</span>
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1rem' }}>
              {relatedServices.map(rs => {
                const RSIcon = ICON_MAP[rs.icon] || Zap;
                return (
                  <Link key={rs.slug} to={`/${rs.slug}`} data-testid={`related-service-${rs.slug}`} style={{
                    background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--rl)',
                    padding: '1.25rem', textDecoration: 'none', transition: 'border-color .2s',
                  }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--green)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                  >
                    <RSIcon size={20} style={{ color: 'var(--red)', marginBottom: '.5rem' }} />
                    <h3 style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: '.95rem', color: 'var(--text)', marginBottom: '.3rem' }}>
                      {rs.h1}
                    </h3>
                    <p style={{ fontSize: '.8rem', color: 'var(--text2)', lineHeight: 1.5 }}>
                      {rs.intro.slice(0, 100)}...
                    </p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* RELATED DISTRICTS */}
      {relatedDistricts.length > 0 && (
        <section className="section" style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)' }}>
          <div className="wrap" style={{ textAlign: 'center' }}>
            <h2 className="h2" style={{ marginBottom: '1.5rem' }}>
              Hangi <span style={{ color: 'var(--green)' }}>İlçedesiniz?</span>
            </h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '.5rem' }}>
              {DISTRICT_PAGES.map(d => (
                <Link key={d.slug} to={`/${d.slug}`} style={{
                  display: 'inline-block', background: 'var(--card)', border: '1px solid var(--border)',
                  borderRadius: 100, padding: '.35rem .9rem', fontSize: '.8rem', color: 'var(--text2)',
                  transition: 'border-color .2s, color .2s', textDecoration: 'none',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--green)'; e.currentTarget.style.color = 'var(--green)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text2)'; }}
                >{d.displayName}</Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RELATED BLOG POSTS */}
      {relatedBlogs.length > 0 && (
        <section className="section" style={{ background: 'var(--bg)', borderTop: '1px solid var(--border)' }}>
          <div className="wrap">
            <h2 className="h2" style={{ marginBottom: '1.5rem' }}>
              İlgili <span style={{ color: 'var(--blue)' }}>Blog Yazıları</span>
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1rem' }}>
              {relatedBlogs.map(blog => (
                <Link key={blog.id} to={`/blog/${blog.slug}`} className="blog-card" style={{
                  background: 'var(--card)', border: '1px solid var(--border)',
                  borderRadius: 'var(--rl)', overflow: 'hidden', textDecoration: 'none', display: 'block',
                }}>
                  <div style={{ height: 140 }}>
                    <img src={blog.image} alt={blog.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                  </div>
                  <div style={{ padding: '1rem' }}>
                    <span style={{ fontSize: '.68rem', color: 'var(--blue)', fontWeight: 600 }}>{blog.category}</span>
                    <h4 style={{
                      fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600,
                      fontSize: '.85rem', color: 'var(--text)', lineHeight: 1.4, marginTop: '.3rem',
                    }}>{blog.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <FinalCTA headline="Hemen Arayın." sub="7/24 Yanınızdayız." />
    </main>
  );
}

function FAQItem({ faq, index }) {
  const [isOpen, setIsOpen] = React.useState(false);
  return (
    <motion.div
      data-testid={`service-faq-${index}`}
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: .35, delay: index * .05 }}
      style={{
        background: 'var(--card)', border: `1px solid ${isOpen ? 'var(--blue-border)' : 'var(--border)'}`,
        borderRadius: 'var(--r)', overflow: 'hidden', transition: 'border-color .25s', cursor: 'pointer',
      }}
      onClick={() => setIsOpen(!isOpen)}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.125rem 1.375rem', gap: '1rem' }}>
        <h3 style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: '.9rem', color: 'var(--text)', lineHeight: 1.4, margin: 0 }}>
          {faq.q}
        </h3>
        <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: .25 }} style={{ flexShrink: 0, color: 'var(--muted)' }}>
          <ChevronDown size={18} />
        </motion.div>
      </div>
      {isOpen && (
        <div style={{ padding: '0 1.375rem 1.25rem', fontSize: '.85rem', color: 'var(--text2)', lineHeight: 1.75 }}>
          {faq.a}
        </div>
      )}
    </motion.div>
  );
}
